<template>
  <div class="search-metiers">
    <PrepeersBreadcrumb
      auto
      section="recherche" />

    <!-- ── HERO + H1 ─────────────────────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ seoPayload?.h1 }}</h1>
        <div
          v-if="seoPayload?.introduction"
          class="hero-section__intro"
          v-html="formatSeoIntro(seoPayload.introduction)" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── BARRE DE RECHERCHE ───────────────────────────────────── -->
    <section class="search-metiers__toolbar-section">
      <div class="container search-metiers__toolbar-inner">
        <PrePeersSearchBar
          v-model="searchDraft"
          :placeholder="t('searchMetiers.searchPlaceholder')"
          :submit-label="t('searchMetiers.searchSubmit')"
          @search="onSubmitSearch" />
      </div>
    </section>

    <!-- ── FILTRES DOMAINES / SECTEURS ─────────────────────────── -->
    <section class="search-metiers__filters-section">
      <div class="container search-metiers__filters-row">
        <PrePeersSelect
          v-model="domainFilter"
          :options="domainSelectOptions"
          :label="t('searchMetiers.domainLabel')" />
        <PrePeersSelect
          v-model="sectorFilter"
          :options="sectorSelectOptions"
          :label="t('searchMetiers.sectorLabel')" />
      </div>
    </section>

    <!-- ── COMPTEUR (total catalogue) ─────────────────────────────── -->
    <section class="search-metiers__count-section">
      <div class="container">
        <p class="search-metiers__count">
          {{ professionCountLabel }}
        </p>
      </div>
    </section>
    <!-- ── GRILLE MÉTIERS ───────────────────────────────────────── -->
    <section class="search-metiers__professions-band">
      <div class="container">
        <div
          v-if="displayedProfessions.length"
          class="search-metiers__profession-grid">
          <UiProfessionCard
            v-for="profession in displayedProfessions"
            :key="profession.id"
            :profession="profession" />
        </div>
        <p
          v-else
          class="search-metiers__empty">
          {{ t('searchMetiers.noResults') }}
        </p>
      </div>
    </section>

    <!-- ── CTA ───────────────────────────────────────────────────── -->
    <section
      v-if="!props.isLoggedIn"
      class="cta-section">
      <div class="container">
        <div class="cta-card">
          <div class="cta-card__avatar">
            <NuxtImg
              src="/images/lola-avatar.png"
              alt="Lola"
              class="cta-card__avatar-img"
              width="72"
              height="72"
              loading="lazy"
              decoding="async"
              format="webp" />
          </div>
          <h3 class="cta-card__title">{{ t('searchMetiers.ctaTitle') }}</h3>
          <p class="cta-card__desc">{{ t('searchMetiers.ctaDesc') }}</p>
          <NuxtLink
            :to="localePath('/discuter')"
            class="cta-card__btn"
            >{{ t('searchMetiers.ctaBtn') }}</NuxtLink
          >
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { LocationQueryRaw, LocationQueryValue } from 'vue-router'
import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref, WritableComputedRef } from 'vue'

import PrePeersSelect from '~/components/core/PrePeersSelect.vue'
import PrePeersSearchBar from '~/components/form/PrePeersSearchBar.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import { METIERS_DOMAIN_OPTIONS, METIERS_SECTOR_OPTIONS } from '~/composables/metiersSearchMockData'
import { useMetiersSearchHub } from '~/composables/useMetiersSearchHub'
import type { PrePeersSelectOption } from '~/types/PrePeersSelect'
import type { ProfessionCardObj } from '~/types/profession'
import type { SearchMetiersProps } from '~/types/SearchMetiers'
import { formatSeoIntro } from '~/utils/helpers'

// ── Props ─────────────────────────────────────────────────────────────

/**
 * Définit les props du composant SearchMetiers.
 */
const props: SearchMetiersProps = defineProps({
  isLoggedIn: {
    type: Boolean,
    default: false,
  },
})

// ── Composables ───────────────────────────────────────────────────────

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const { seoPayload, filterProfessions, publicTotalCount } = useMetiersSearchHub()

/**
 * Lit un paramètre de query Vue Router en chaîne (première valeur si tableau).
 * @param {LocationQueryValue | LocationQueryValue[] | undefined} param - Valeur brute de `route.query`.
 * @returns {string} Chaîne normalisée (vide si absente).
 */
function readRouteQueryParam(param: LocationQueryValue | LocationQueryValue[] | undefined): string {
  const value = Array.isArray(param) ? param[0] : param
  if (value == null || value === '') return ''
  return String(value)
}

/**
 * Clone `route.query` pour une navigation typée (`LocationQueryRaw`).
 * @returns {LocationQueryRaw} Copie mutable de la query courante.
 */
function cloneRouteQuery(): LocationQueryRaw {
  return { ...route.query } as LocationQueryRaw
}

/**
 * Met à jour la query courante (ajout ou suppression de clés).
 * @param {Record<string, string | undefined>} patch - Clés à définir ; valeur vide → suppression.
 */
function patchRouteQuery(patch: Record<string, string | undefined>): void {
  const base: LocationQueryRaw = cloneRouteQuery()
  const keysToRemove: Set<string> = new Set(
    Object.entries(patch)
      .filter(([, value]: [string, string | undefined]): boolean => !value)
      .map(([key]: [string, string | undefined]): string => key),
  )
  const query: LocationQueryRaw = Object.fromEntries(
    Object.entries(base).filter(([key]: [string, unknown]): boolean => !keysToRemove.has(key)),
  ) as LocationQueryRaw
  for (const [key, value] of Object.entries(patch)) {
    if (value) {
      query[key] = value
    }
  }
  void router.push({ path: route.path, query })
}

// ── Refs ──────────────────────────────────────────────────────────────

const searchDraft: Ref<string> = ref<string>('')
const appliedSearch: Ref<string> = ref<string>('')

// ── ComputedRef ───────────────────────────────────────────────────────

const domainSelectOptions: ComputedRef<PrePeersSelectOption[]> = computed(
  (): PrePeersSelectOption[] => {
    const allLabel: string = t('searchMetiers.domainAll')
    return METIERS_DOMAIN_OPTIONS.map(
      (o: PrePeersSelectOption, index: number): PrePeersSelectOption =>
        index === 0 ? { value: '', label: allLabel } : o,
    )
  },
)

const sectorSelectOptions: ComputedRef<PrePeersSelectOption[]> = computed(
  (): PrePeersSelectOption[] => {
    const allLabel: string = t('searchMetiers.sectorAll')
    return METIERS_SECTOR_OPTIONS.map(
      (o: PrePeersSelectOption, index: number): PrePeersSelectOption =>
        index === 0 ? { value: '', label: allLabel } : o,
    )
  },
)

const domainFilter: WritableComputedRef<string> = computed({
  get(): string {
    return readRouteQueryParam(route.query.domain)
  },
  set(value: string): void {
    patchRouteQuery({ domain: value || undefined })
  },
})

const sectorFilter: WritableComputedRef<string> = computed({
  get(): string {
    return readRouteQueryParam(route.query.sector)
  },
  set(value: string): void {
    patchRouteQuery({ sector: value || undefined })
  },
})

const displayedProfessions: ComputedRef<ProfessionCardObj[]> = computed((): ProfessionCardObj[] =>
  filterProfessions(appliedSearch.value, domainFilter.value, sectorFilter.value),
)

const professionCountLabel: ComputedRef<string> = computed((): string =>
  t('searchMetiers.professionCount', { n: publicTotalCount }),
)

const seoTitle: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.title,
)

const seoDescription: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.description,
)

const seoOgTitle: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.h1,
)

const seoOgDescription: ComputedRef<string | undefined> = computed(
  (): string | undefined => seoPayload.value?.description,
)

// ── Fonctions ─────────────────────────────────────────────────────────

/**
 * Synchronise le champ de recherche et l'état appliqué avec le paramètre d'URL `q`.
 * @returns {void}
 */
function syncSearchFromRoute(): void {
  const v = readRouteQueryParam(route.query.q)
  searchDraft.value = v
  appliedSearch.value = v
}

/**
 * Soumet la recherche texte et met à jour la query `q`.
 * @param {string} value - Texte (trim côté barre).
 * @returns {void}
 */
function onSubmitSearch(value: string): void {
  appliedSearch.value = value
  patchRouteQuery({ q: value || undefined })
}

// ── Watch ───────────────────────────────────────────────────────────────

watch(
  () => readRouteQueryParam(route.query.q),
  (): void => {
    syncSearchFromRoute()
  },
  { immediate: true },
)

// ── SEO meta ──────────────────────────────────────────────────────────

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: seoOgTitle,
  ogDescription: seoOgDescription,
  ogType: 'website',
})
</script>

<style scoped>
.search-metiers__toolbar-section {
  padding: var(--space-10) var(--container-pad) var(--space-6);
}

.search-metiers__toolbar-inner {
  max-width: var(--container-md);
}

.search-metiers__filters-section {
  padding: 0 var(--container-pad) var(--space-8);
}

.search-metiers__filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  max-width: var(--container-xl);
  margin: 0 auto;
  align-items: flex-end;
}

.search-metiers__count-section {
  padding: 0 var(--container-pad) var(--space-8);
}

.search-metiers__count {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-primary);
  max-width: var(--container-xl);
}

.search-metiers__professions-band {
  padding: var(--space-12) var(--container-pad) var(--space-20);
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.12) 0%,
    rgba(0, 0, 0, 0.22) 40%,
    rgba(0, 0, 0, 0.16) 100%
  );
  border-top: 1px solid var(--border-subtle);
}

.search-metiers__profession-grid {
  display: grid;
  grid-template-columns: 1fr;
  align-items: stretch;
  gap: var(--space-6);
  max-width: var(--container-xl);
  margin: 0 auto;
}

@media (min-width: 640px) {
  .search-metiers__profession-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .search-metiers__profession-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.search-metiers__empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--text-md);
  margin: var(--space-12) 0;
}
</style>
